import React, { createContext, useCallback, useContext, useLayoutEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HiXMark } from 'react-icons/hi2';
import { isCopilotConfigured } from './api/copilot-api';
import CopilotPanel from './components/copilot-panel';
import CopilotErrorBoundary from './components/copilot-error-boundary';
import useCopilot from './hooks/use-copilot';
import useCopilotScope from './hooks/use-copilot-scope';
import useRouteAction from './hooks/use-route-action';
import { copilotPathFor, isCopilotPath } from './model/copilot-paths';
import { resourcePathFor } from './model/resource-focus';
import useUnpublishedApp from './hooks/use-unpublished-app';
import { SHOW_PUBLISH_STEPPER } from './components/publish-stepper';

const WorkspaceContext = createContext(null);
export const useCopilotWorkspace = () => useContext(WorkspaceContext);

/** Mount above the page router: navigating to a resource must not restart the runtime. */
export default function CopilotWorkspace({ children, onPanelOpenChange, onStepperOpenChange }) {
    return isCopilotConfigured() ? <Workspace onPanelOpenChange={onPanelOpenChange} onStepperOpenChange={onStepperOpenChange}>{children}</Workspace> : children;
}

function Workspace({ children, onPanelOpenChange, onStepperOpenChange }) {
    const scopeInfo = useCopilotScope();
    // A new application gets its own runtime, with no previous application's transient state.
    return <ScopedWorkspace key={scopeInfo.scope || 'loading'} scopeInfo={scopeInfo} onPanelOpenChange={onPanelOpenChange} onStepperOpenChange={onStepperOpenChange}>{children}</ScopedWorkspace>;
}

function ScopedWorkspace({ children, scopeInfo, onPanelOpenChange, onStepperOpenChange }) {
    const { scope, appId, isAccountScope } = scopeInfo;
    const location = useLocation();
    const consolePage = isCopilotPath(location.pathname);
    // The publish stepper is disabled for now (see SHOW_PUBLISH_STEPPER).
    const stepperOpen = useUnpublishedApp(appId, SHOW_PUBLISH_STEPPER && consolePage);
    const simulator = /^\/app\/[^/]+\/agent-preview\/?$/.test(location.pathname);
    const [navigation, setNavigation] = useState({ key: null, attached: false, activated: false });
    // Process each history entry once, including browser Back/Forward. Subsequent resource
    // navigation retains the panel within this scope; a normal first visit never opens it.
    let current = navigation;
    if (navigation.key !== location.key) {
        const attached = !consolePage && !simulator && (location.state?.fromCopilot === true || navigation.attached);
        current = { key: location.key, attached, activated: navigation.activated || consolePage || attached };
        setNavigation(current);
    }
    const onAction = useRouteAction(scopeInfo, { followSameScope: true, enabled: consolePage || current.attached });
    const onResourceFocus = useCallback(resource => {
        const path = resourcePathFor(resource, appId);
        if (path) onAction({ type: 'ROUTE', path });
    }, [appId, onAction]);
    const copilot = useCopilot(scope, Boolean(scope) && current.activated && !simulator, { onAction, onResourceFocus });
    const [composer, setComposer] = useState({});
    const setComposerText = useCallback(text => setComposer({ sessionId: copilot.sessionId, text }), [copilot.sessionId]);
    const sharedCopilot = {
        ...copilot,
        composerText: composer.sessionId === copilot.sessionId ? composer.text : '',
        setComposerText,
    };
    const attached = current.attached;
    // Keep the dashboard header and navigation in sync before the panel is painted.
    useLayoutEffect(() => {
        onPanelOpenChange?.(attached);
        return () => onPanelOpenChange?.(false);
    }, [attached, location.key, onPanelOpenChange]);

    useLayoutEffect(() => {
        onStepperOpenChange?.(stepperOpen);
        return () => onStepperOpenChange?.(false);
    }, [stepperOpen, onStepperOpenChange]);

    return (
        <WorkspaceContext.Provider value={{ copilot: sharedCopilot, scopeInfo, stepperOpen }}>
            <div className={attached ? 'flex h-full min-h-0 flex-col overflow-hidden md:flex-row' : 'h-full min-h-0'}>
                {attached && (
                    <aside aria-label="Copilot workspace" className="relative isolate flex h-[45vh] min-h-[260px] shrink-0 flex-col rounded-lg bg-white p-2 md:h-full md:w-[380px] xl:w-[420px]">
                        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-20 rounded-lg shadow-[inset_0_0_24px_3px_rgba(186,230,26,0.28)] dark:shadow-[inset_0_0_24px_3px_rgba(186,230,26,0.20)]" />
                        <div className="min-h-0 flex-1">
                            <CopilotErrorBoundary fallback={({ reset }) => <div role="alert" className="p-4 text-sm">Copilot could not load. <button type="button" onClick={reset} className="underline">Try again</button></div>}>
                                <CopilotPanel
                                    embedded
                                    docked
                                    copilot={sharedCopilot}
                                    isAccountScope={isAccountScope}
                                    ready={Boolean(scope)}
                                    headerActions={<>
                                        <Link to={copilotPathFor(appId)} className="shrink-0 whitespace-nowrap rounded-lg px-2 py-2 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900">
                                            Back to Console
                                        </Link>
                                        <button type="button" onClick={() => setNavigation(previous => ({ ...previous, attached: false }))} aria-label="Close copilot" title="Close copilot" className="shrink-0 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900">
                                            <HiXMark className="h-4 w-4" />
                                        </button>
                                    </>}
                                />
                            </CopilotErrorBoundary>
                        </div>
                    </aside>
                )}
                <div className={attached ? 'min-h-0 min-w-0 flex-1 overflow-auto' : 'h-full min-h-0'}>{children}</div>
            </div>
        </WorkspaceContext.Provider>
    );
}
